import { Textarea } from '@monorepo/base/src/components/form/textarea/textarea';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { observer } from 'mobx-react';
import React, { FC, useState, useMemo, useEffect } from 'react';
import styles from './bidder-domains-states.module.scss';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { ControlledTable } from '@monorepo/controlled/src/components/table-wrapper/table-wrapper';
import { Dropdown, option } from '@monorepo/base/src/components/dropdown/dropdown';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IAdminxStore } from '../../../modules/stores';
import { useBidderDomainsStatesHeaders } from '../../../modules/hooks/theaders/bidder-domains-states.headers';
import { useBidderDomainsStatesActions } from '../../../modules/hooks/actions/bidder-domains-states.actions';
import { useStateBetweenPages } from '../../../modules/hooks/tools/use-state-between-pages';
import { useList } from '../../../modules/hooks/tools/use-list';
import { BidderCampaignTableModel } from '../../../modules/models/bidder-campaign/bidder-camapign.model';
import { sortData } from '../../../modules/hooks/tools/use-merge-with-performance';
import { usePageSettings } from '@monorepo/tools/src/lib/hooks/tools/use-page-settings';
import { BidderDomainsApi } from '../../../modules/apis/bidder-domains.api';
import { ICampaignDomainState } from '../../../modules/models/bidder-domains.model';
import { useRoute } from '../../../modules/hooks/tools/use-route';
import { SpacerY } from '@monorepo/base/src/components/spacer/spacer';
import StatsFilters, { StatsFilterOption, toDomainStatusOptions } from '../../../modules/components/stats-filters/stats-filters';
import { determineButtonProperties } from '../../../modules/utils/bidder-domains-states.utils';

const pageBarDebugProps = { dataAttrs: [new DataAttribute('id', 'bidder-domains-states')] };

export const BidderDomainsStates: FC = observer(() => {
	const { bidderStore } = useStores<IAdminxStore>();
	const [error, setError] = useState<string>('');
	const [domainsForm, setDomainsForm] = useState<string>('');
	const [isLoadingAnalyze, setIsLoadingAnalyze] = useState<boolean>(false);
	const [selectedCampaign, setSelectedCampaign] = useState<BidderCampaignTableModel | undefined>(undefined);
	const [domainResults, setDomainResults] = useState<ICampaignDomainState[]>([]);
	const [status, setStatus] = useState<string>('');
	const [pausedInCache, setPausedInCache] = useState<string>('');
	const [statusOptions, setStatusOptions] = useState<StatsFilterOption[]>([]);
	const sortBy = usePageSettings().tableStore?.getSortingBy();

	useEffect(() => {
		let cancelled = false;

		BidderDomainsApi.getDomainStatuses()
			.then(statuses => {
				if (cancelled) {
					return;
				}
				setStatusOptions(toDomainStatusOptions(statuses));
			})
			.catch(error => {
				console.error('Failed to load domain statuses', error);
				if (!cancelled) {
					setStatusOptions([]);
				}
			});

		return () => {
			cancelled = true;
		};
	}, []);

	const filteredDomainResults = useMemo(() => {
		let filtered = domainResults;

		if (status && status !== '') {
			filtered = filtered.filter(domain => {
				return domain.status === status;
			});
		}

		if (pausedInCache && pausedInCache !== '') {
			filtered = filtered.filter(domain => {
				if (pausedInCache === 'Yes') {
					return domain.paused;
				} else if (pausedInCache === 'No') {
					return !domain.paused;
				}
				return true;
			});
		}

		// Add computed action name to each domain for sorting
		return filtered.map(domain => {
			const { buttonProperties } = determineButtonProperties(domain.paused, domain.status);
			return {
				...domain,
				action: buttonProperties.actionName,
			};
		});
	}, [domainResults, status, pausedInCache]);

	const sortedData = sortData(filteredDomainResults, sortBy?.[0]);
	const bidderStateBetweenPages = useStateBetweenPages(() => bidderStore.campaignsList.getList().length !== 0);
	useList<BidderCampaignTableModel>(bidderStore.campaignsList, undefined, bidderStateBetweenPages);

	const analyzeDomainsStates = async () => {
		if (!selectedCampaign) {
			setError('Please select a campaign first');
			return;
		}

		setIsLoadingAnalyze(true);
		const domainsArray = domainsForm
			.split('\n')
			.map(domain => domain.trim())
			.filter(domain => domain.length > 0);

		if (!domainsArray.length) {
			setIsLoadingAnalyze(false);
			setError('Please enter domain addresses');
			return;
		}

		try {
			const response = await BidderDomainsApi.getCampaignDomainStatus({
				id: selectedCampaign.id,
				domains: domainsArray,
			});
			setDomainResults(response.domains);
			setError('');
		} catch (err) {
			setError('Failed to analyze domains');
			console.error('Error analyzing domains:', err);
		} finally {
			setIsLoadingAnalyze(false);
		}
	};

	const { columns } = useBidderDomainsStatesHeaders(selectedCampaign?.id, analyzeDomainsStates);
	const { currentRouteWithoutSpecialChars } = useRoute();
	const { SelectedActions } = useBidderDomainsStatesActions({
		campaignId: selectedCampaign?.id,
		debugProps: { dataAttrs: [new DataAttribute('id', 'bidder_domains_actions')] },
		onRefresh: analyzeDomainsStates,
	});
	const sortedCampaigns = sortData(bidderStore.campaignsList.getList().slice(), sortBy?.[0]);
	const campaignOptions = sortedCampaigns.map(campaign => `${campaign.id} - ${campaign.name}`);

	const onCampaignSelect = (options: Set<option> | undefined) => {
		if (!options) {
			return;
		}

		const optionsArr = Array.from(options);
		const selectedOption = optionsArr[0];
		if (selectedOption) {
			const campaignId = parseInt(selectedOption.split(' - ')[0]);
			const campaign = sortedCampaigns.find(c => c.id === campaignId);
			setSelectedCampaign(campaign);
			setError('');
			setDomainResults([]);
		}
	};

	const handleFilterChange = (filterType: 'status' | 'paused', value: string) => {
		if (filterType === 'status') {
			setStatus(value);
		} else if (filterType === 'paused') {
			setPausedInCache(value);
		}
	};

	return (
		<Page>
			<Pagebar debugProps={pageBarDebugProps}>
				<PagebarTitle>Bidder Domains States</PagebarTitle>
			</Pagebar>

			<div className={styles.wrapper}>
				<Dropdown
					label={'Select Campaign'}
					placeholder={'Search By CampaignID...'}
					options={campaignOptions}
					onSelect={onCampaignSelect}
					disabled={bidderStore.campaignsList.getIsLoading()}
					autocomplete={true}
					autocompletePlaceholder={'Search By CampaignID...'}
					isLoading={bidderStore.campaignsList.getIsLoading()}
					debugProps={{ dataAttrs: [new DataAttribute('id', 'campaign_search_dropdown')] }}
				/>
				<SpacerY />

				<Textarea
					rows={10}
					inputWrapperClassName={styles.textareaWrapper}
					value={domainsForm}
					placeholder={'Type Domain Addresses separated by `Enter`, eg:\nexample.com\nanother-domain.com'}
					onValue={value => {
						if (error) {
							setError('');
						}
						setDomainsForm(value);
					}}
					debugProps={{ dataAttrs: [new DataAttribute('id', 'bidder_domains_stats_text_area')] }}
				/>
				<PrimaryButton
					disabled={!domainsForm || !selectedCampaign || isLoadingAnalyze}
					onClick={analyzeDomainsStates}
					className={styles.analayzeBtn}
					icon={'search-sm'}
					isRotateIcon={false}>
					Analyze
				</PrimaryButton>
				<SpacerY />

				{domainResults.length > 0 && (
					<ControlledTable
						classes={{ wrapper: styles.table }}
						currentRoute={currentRouteWithoutSpecialChars}
						columns={columns}
						data={sortedData}
						defaultSortBy={sortBy}
						isLoading={isLoadingAnalyze}
						statsFilters={
							<StatsFilters
								statusValue={status}
								pausedValue={pausedInCache}
								statusOptions={statusOptions}
								onFilterChange={handleFilterChange}
							/>
						}>
						{() => {
							return {
								SelectedActions,
							};
						}}
					</ControlledTable>
				)}
				{error && <div className={styles.errorMsg}>{error}</div>}
			</div>
		</Page>
	);
});
